import {
	AfterViewInit,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { FullCalendarComponent } from '@fullcalendar/angular';
import {
	Calendar,
	CalendarOptions,
	DateSelectArg,
	DayCellMountArg,
	EventClickArg,
	EventHoveringArg,
	EventInput,
} from '@fullcalendar/core';
//NOTE: Plugins in v5 must be after import calendar from core
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';

import arLocale from '@fullcalendar/core/locales/ar';
import arSaLocale from '@fullcalendar/core/locales/ar-sa';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { DateClickArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Utils } from 'formiojs';
import _cloneDeep from 'lodash/cloneDeep';
import * as moment from 'moment';
import {
	BehaviorSubject,
	delay,
	distinctUntilChanged,
	filter,
	Observable,
	Subject,
	take,
	takeUntil,
} from 'rxjs';
import {
	ConfirmationDialogComponent,
	FormioCustomComponent,
	FormioLoader,
} from 'dp-formio';
import { DEFAULT_OPTIONS } from './calendar.options';
import { CustomCalenderOptions, DialogEventHook, RedirectObjType } from './models';
import { DialogData, DialogResult } from './models/dialog-data';
import { ScheduleDialogComponent } from './schedule-dialog/schedule-dialog.component';
@Component({
  selector: 'app-calendar-wrapper',
  templateUrl: './calendar-wrapper.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [FormioLoader],
})
export class CalenderWrapperComponent
  implements FormioCustomComponent<any>, OnInit, OnChanges, OnDestroy, AfterViewInit
{
  lang = this.translateService.currentLang;
  dir: 'rtl' | 'ltr';
  @Input() value: any = {};
  @Output() valueChange = new EventEmitter<any>();

  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() viewOnly: boolean;
  @Input() data: any;
  @Input() customOptions: Partial<CustomCalenderOptions>;
  @Input() formioComponent: any;

  @ViewChild('calendar') fullCalendar: FullCalendarComponent;
  calendarApi: Calendar;
  private _isOpTionsReadySub = new BehaviorSubject(false);
  isOpTionsReady$ = this._isOpTionsReadySub.asObservable();

  destroy$ = new Subject<void>();
  calendarOptions: CalendarOptions;
  customCalendarOptions = {};
  calendarEvents = [];

  constructor(
    private formioLoader: FormioLoader,
    private translateService: TranslateService,
    public dialog: MatDialog,
    public router: Router,
    public route: ActivatedRoute
  ) {
    forwardRef(() => Calendar);
  }

  ngOnInit() {
    this._subscribeToLanguage();
    this._prepareEvents();
    this._triggerResize();
  }

  ngAfterViewInit(): void {
    this.isOpTionsReady$
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => data),
        delay(50)
      )
      .subscribe((res) => {
        this.calendarApi = this.fullCalendar.getApi();
        this._goToDate();
      });
  }
  private _goToDate() {
    const jumpToDate = this.customOptions?.jumpToDate?.active;
    if (this.calendarApi && jumpToDate) {
      const date = this._evaluate(
        {
          _string: this.customOptions?.jumpToDate.dataKey,
        },
        ''
      );

      if (date) {
        this.calendarApi.gotoDate(date);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.customOptions) {
      this._isOpTionsReadySub.next(false);
      this._initCalendar();
    }

    if (this.formioComponent?.visible && changes.value) {
      this._triggerResize();
    }

    if (changes.value) {
      this._prepareEvents();
    }

    if ((changes.viewOnly || changes.value) && this.calendarApi) {
      this.calendarApi.render();
    }
  }

  notifyValue(value = this.calendarEvents) {
    this.calendarEvents = value;
    this.calendarOptions.events = this.calendarEvents;
    this.value = _cloneDeep({ events: value });
    this.valueChange.emit(this.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _subscribeToLanguage() {
    (this.translateService.onLangChange as Observable<LangChangeEvent>)
      .pipe(
        distinctUntilChanged((prev, curr) => prev.lang === curr.lang),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        this.dir = data.lang === 'ar' ? 'rtl' : 'ltr';
        this.lang = data.lang;
      });
  }

  private _triggerResize() {
    //workaround for calendar init
    setTimeout(function () {
      window.dispatchEvent(new Event('resize'));
    }, 1);
  }

  private _canEdit() {
    return this.customOptions?.actions?.edit ?? false;
  }
  private _canAdd() {
    return this.customOptions?.actions?.add ?? false;
  }
  private _canDelete() {
    return this.customOptions?.actions?.delete ?? false;
  }
  private _canClear() {
    return this.customOptions?.actions?.clear ?? false;
  }
  private _canView() {
    return this.customOptions?.actions?.view ?? true;
  }
  private _showAddButton() {
    return this.customOptions?.buttons?.add ?? false;
  }
  private _showDateTypeBtns() {
    return this.customOptions?.buttons?.dateType ?? false;
  }

  private _prepareEvents() {
    this.calendarEvents = this.value?.events ?? [];
    if (this.calendarOptions?.events) {
      this.calendarOptions.events = this.calendarEvents;
    }
  }

  private _initCalendar(options = DEFAULT_OPTIONS) {
    const customButtons = [
      this._canClear() && !this.viewOnly ? 'clearEvents' : '',
      this._showAddButton() && !this.viewOnly ? 'addEvent' : '',
    ]
      .filter((btn) => !!btn)
      .join(',');

    const dateButtons = [
      this._showDateTypeBtns() && 'hijri',
      this._showDateTypeBtns() && 'gregorian',
    ]
      .filter((btn) => !!btn)
      .join(',');

    this.calendarOptions = {
      ...options,
      plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
      customButtons: {
        clearEvents: {
          text: this.translateService.instant('forms.common.clear'),
          click: () => {
            this._clearAllEvents();
          },
        },
        addEvent: {
          text: this.translateService.instant('forms.common.addEvent'),
          click: (event) => {
            if (this._canAdd() && !this.viewOnly) {
              this._openEventDialog({}, 'date');
            }
          },
        },
        gregorian: {
          text: this.translateService.instant('gregorian'),
          click: () => {
            this.calendarOptions.locale = this.lang;
          },
        },
        hijri: {
          text: this.translateService.instant('hijri'),
          click: () => {
            this.calendarOptions.locale = 'ar-sa';
          },
        },
      },
      headerToolbar: {
        left:
          `prev,next` +
          (customButtons ? ' ' + customButtons : '') +
          (dateButtons ? +' ' + dateButtons : ''),
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay',
      },

      events: this.calendarEvents,
      locale: this.lang,
      locales: [arLocale, enLocale, arSaLocale],
      ...(this.customOptions?.calendar ?? {}),
      eventClick: (args) => this._handleEventClick(args),
      eventResize: (args) => this._handleEventResize(args),
      select: (args) => this._handleSelect(args),
      navLinkDayClick: (date, jsEvent) => this._handleDayClick(date, jsEvent),
      eventMouseEnter: (args) => {
        this._handleEventMouseEnter(args);
      },
      eventMouseLeave: (args) => {
        this._handleEventMouseLeave(args);
      },
      dateClick: (args) => {
        let _continue = true;
        const _hook = this.customOptions.hooks?.dateClick;
        if (_hook && _hook.active) {
          _continue = this._evaluate(
            {
              _string: _hook.dataKey,
              params: {
                date: args.date,
                moment: moment,
              },
            },
            null
          );
        }
        if (_continue) {
          this._handleDateClick(args);
        }
      },
      dayCellDidMount: (args: DayCellMountArg) => {
        const _hook = this.customOptions.hooks?.dayCellDidMount;
        if (_hook && _hook.active) {
          this._evaluate(
            {
              _string: _hook.dataKey,
              params: {
                el: args.el,
                date: args.date,
                moment: moment,
              },
            },
            ''
          );
        }
      },
      eventContent: (arg, ce) => {
        const titleText = this.translateService.instant(
          arg.event?.title || arg.event?.extendedProps.data?.sessionType
        );

        const isSessionRequired =
          arg.event.extendedProps?.data?.data?.requiredSession || false;

        const childs = [];
        if (isSessionRequired) {
          childs.push(ce('b', { style: { color: 'red' } }, ' * '));
        }

        const content = ce(
          'div',
          {
            class: 'd-flex align-items-center',
          },
          null,
          [
            ce(
              'div',
              {
                class: 'fc-daygrid-event-dot',
                style: !this.viewOnly ? { borderColor: arg.event.borderColor } : {},
              },
              null
            ),
            ce('div', { class: 'fc-event-time' }, arg.timeText),
            ce('div', { class: 'fc-event-title' }, titleText, [childs]),
          ]
        );
        return content;
      },
    };
    this._isOpTionsReadySub.next(true);
  }

  //#region handlers

  private _handleEventMouseEnter(args: EventHoveringArg) {
    const isRequired = args.event.extendedProps?.data?.data?.requiredSession || false;

    if (isRequired || this.viewOnly || !this._canDelete()) {
      return;
    }

    const btn = this._appendDeleteButton(args.el);
    btn.onclick = (event) => {
      event.stopPropagation();
      event.preventDefault();
      this._openConfirmDialog(
        this.translateService.instant('forms.common.deleteEventMsg')
      ).subscribe((res) => {
        this._deleteEvent(args.event.id);
      });
    };
  }

  private _handleEventMouseLeave(args: EventHoveringArg) {
    if (this._canDelete() && !this.viewOnly) {
      const el = args.el;
      const btn = el.querySelector('#delete-event');
      if (btn) {
        el.removeChild(btn);
      }
    }
  }

  private _handleEventResize(args: EventResizeDoneArg): void {
    if (args.event.display.match(/background/)) {
      return null;
    }
    if (this._canEdit() && !this.viewOnly) {
      this._openEventDialog(args, 'resize');
    }
  }

  private _handleDayClick(date: Date, jsEvent: any): void {
    if (this._canDelete() && !this.viewOnly) {
      this._openConfirmDialog(
        this.translateService.instant('forms.common.clearDayMsg')
      ).subscribe((res) => {
        this.calendarEvents = this._clearDayEvents(date);
        this.notifyValue(this.calendarEvents);
      });
      return null;
    }
    this.calendarApi.changeView('timeGridDay', date.toISOString());
  }

  private _handleEventClick(arg: EventClickArg): void {
    if (arg.event.display.match(/background/)) {
      return null;
    }
    const event = this.calendarEvents.find((ev) => ev.id === arg.event.id);
    const redirect = this.customOptions?.event?.redirect;
    if (redirect && redirect?.url) {
      this._redirectEvent(event, redirect);
      return;
    }

    const canOpenDialog = this.customOptions?.event?.openDialog;
    if (canOpenDialog?.active) {
      const data = Utils.evaluate(canOpenDialog.dataKey, {
        submission: this.data,
        event: event,
        moment: moment,
      });
      if (!data) {
        return;
      }
    }

    if (this._canView()) {
      this._openEventDialog(event, 'event');
    }
  }

  private _redirectEvent(event: EventInput, redirectObj: RedirectObjType) {
    const evaluatedUrl = this._evaluate(
      {
        _string: redirectObj?.url,
        params: {
          event: event,
          route: this.route.snapshot,
        },
      },
      ''
    );
    if (redirectObj.external) {
      window.open(evaluatedUrl);
      return;
    }
    this.router.navigate(evaluatedUrl, {
      ...(redirectObj.relative ? { relativeTo: this.route } : {}),
      ...(redirectObj.skipLocationChange ? { skipLocationChange: true } : {}),
    });
  }

  private _handleSelect(arg: DateSelectArg) {
    //Note  select and click not working together
    if (this._canAdd() && !this.viewOnly) {
      const event = {
        ...arg,
        data: {
          start: arg.start,
          end: arg.end,
        },
      };
      this._openEventDialog(event, 'select');
    }
  }

  private _handleDateClick(arg: DateClickArg) {
    const dialogHook = this.customOptions?.dialogHook ?? 'afterSubmit';
    if (this._canAdd() && !this.viewOnly && !this._showAddButton()) {
      const event = {
        ...arg,
        data: {
          ...(dialogHook === 'afterSubmit'
            ? { data: { start: arg.dateStr, end: arg.dateStr } }
            : { start: arg.dateStr, end: arg.dateStr }),
        },
      };

      this._openEventDialog(event, 'date');
    }
  }

  //#endregion

  private _openEventDialog(event: any, type: DialogData['type']) {
    const dialogHook = this.customOptions?.dialogHook ?? 'afterSubmit';
    const _evaluate = this._evaluate({
      params: { event: event, type: type },
    });
    let calculatedSubmission = { rootData: this.data, data: {} };
    if (this.customOptions?.event?.dialogForm?.override) {
      calculatedSubmission = Object.assign(calculatedSubmission, _evaluate);
    } else {
      if (dialogHook === 'afterSubmit') {
        calculatedSubmission = {
          ...calculatedSubmission,
          ...event?.data,
          data: {
            ...(event?.data?.data ?? {}),
            ...(_evaluate?.data ?? {}),
          },
        };
      } else {
        calculatedSubmission = {
          ...calculatedSubmission,
          data: {
            ...(event?.data ?? {}),
            ...(_evaluate?.data ?? {}),
          },
        };
      }
    }

    const dialogCustomOptions = this.customOptions?.dialog?.options ?? {};
    const dialog = this.dialog.open(ScheduleDialogComponent, {
      width: 'min(80%,900px)',
      direction: this.dir,
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: false,
      ...dialogCustomOptions,
      data: {
        formKey: this.customOptions?.event?.formKey ?? '',
        type: type,
        event: event,
        options: this.customOptions,
        viewOnly: this.viewOnly,
        disabled: this.disabled,
        submission: calculatedSubmission,
        dialogHook,
      },
    });
    dialog
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        filter((rs) => !!rs)
      )
      .subscribe((res: DialogResult) => {
        if (type === 'date' || type === 'select') {
          this._addEvent(res);
        }
        if (type === 'event') {
          this._updateEvent(res, event?.id);
        }
      });
  }

  private _openConfirmDialog(message = '', ok: string = null, cancel: string = null) {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        width: 'min(80% , 250px)',
        direction: this.dir,
        restoreFocus: false,
        data: {
          message: message,
          buttonText: {
            cancel: cancel ?? this.translateService.instant('forms.common.cancel'),
            ok: ok ?? this.translateService.instant('forms.common.yes'),
          },
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((rs) => !!rs)
      );
  }

  //#region  events
  private _addEvent({ data, hook }: DialogResult) {
    const dialogHook = hook ?? 'afterSubmit';
    let id = undefined;
    if (dialogHook === 'afterSubmit') {
      id = data?.data?.sessionId ?? data?.data?.id ?? data?._id;
    } else {
      id = data?.sessionId ?? data?.id;
    }
    id ??= Utils.guid();
    const eventData = this._mapToEventObject({ data, id, dialogHook });
    this.notifyValue([...this.calendarEvents, eventData]);
  }

  private _updateEvent({ data, hook }: DialogResult, id: any) {
    const dialogHook: DialogEventHook = hook ?? 'afterSubmit';
    const index = this.calendarEvents.findIndex((itm) => itm.id === id);
    if (index >= 0) {
      this.calendarEvents[index] = this._mapToEventObject({
        data,
        id,
        dialogHook,
      });
      this.notifyValue(this.calendarEvents);
    }
  }

  private _mapToEventObject(event: {
    data: any;
    id?: any;
    dialogHook?: DialogEventHook;
  }) {
    const style = this.customOptions?.event?.style ?? {};

    const colors = {
      invalid: this.customOptions.colors?.invalid ?? 'var(--invalid-color, red)',
      valid:
        this.customOptions.colors?.valid ??
        'var(--valid-color,var(--primaryColor , green))',
    };

    const _data = event?.dialogHook === 'afterSubmit' ? event.data?.data : event.data;

    return {
      id: event.id,
      color: _data?.Submit ? colors.valid : colors.invalid,
      ...style,
      ...(_data?.title ? { title: _data?.title } : {}),
      ...(_data?.start ? { start: this._toUtcDate(_data?.start) } : {}),
      ...(_data?.end ? { end: this._toUtcDate(_data?.end) } : {}),
      data: event.data,
    };
  }

  private _deleteEvent(id) {
    const data = this.calendarEvents.filter((event) => event?.id !== id);
    this.notifyValue(data);
  }

  private _clearAllEvents() {
    this._openConfirmDialog(
      this.translateService.instant('forms.common.clearEventsMsg')
    ).subscribe((res) => {
      const events = this.calendarEvents.filter(
        (event) => event.data?.data?.requiredSession
      );

      this.calendarEvents = events;
      this.notifyValue(this.calendarEvents);
    });
  }

  private _clearDayEvents(startDate) {
    const startDay = moment(startDate).startOf('day');
    const endDay = moment(startDate).endOf('day');
    const inDay = (date) =>
      moment(date).isSame(startDay) ||
      (moment(date).isAfter(startDay) && moment(date).isBefore(endDay));
    return this.calendarEvents.filter((event) => {
      const start = inDay(this._toLocalDate(event.start));
      if ('end' in event) {
        const end = inDay(this._toLocalDate(event.end));
        return !(start && end) || event?.data?.data?.requiredSession;
      }
      return !start || event?.data?.data?.requiredSession;
    });
  }

  //#endregion

  private _appendDeleteButton(el: HTMLElement) {
    const btn = document.createElement('button');
    btn.className = 'delete-event';
    btn.innerHTML = '<i class="fa fa-times"></i>';
    btn.id = 'delete-event';
    el.appendChild(btn);
    return btn;
  }

  private _evaluate(
    { _string = this.customOptions?.event?.dialogForm?.data, params = {} } = {},
    emptyObj: any = { data: {} }
  ) {
    try {
      return _string
        ? Utils.evaluate(_string, { submission: this.data, ...params }) || emptyObj
        : emptyObj;
    } catch (error) {
      return emptyObj;
    }
  }

  _toUtcDate(date) {
    return moment.utc(new Date(date)).format();
  }

  _toLocalDate(date) {
    return moment.utc(date).local();
  }
}
